import styled from "styled-components";
import { Flex, Box } from "rebass/styled-components";
import { ApolloError, NetworkStatus } from "@apollo/client";

import { Clip, NoFilterResultsContainer } from "./Components";
import { InfiniteScrollPagination } from "./Components/InfiniteScrollPagination";
import { PinnedClips } from "MVVM/Views/Profile/Clips/Components/PinnedClips";
import { PageInfo } from "MVVM/Views/MatchHistory/@types";
import { MatchHistoryWidget } from "MVVM/Views/MatchHistory/MatchHistoryWidget";
import { LoadingSkeleton } from "MVVM/Components/Skeloton";
import { MainRail } from "MVVM/Components/Wrappers";
import { LoadingSpinner } from "MVVM/Components/Lottie";
import { EClipsViews } from "MVVM/@types";

type Reaction = {};

type User = {
  _id: string;
  id: string;
  avatar: string;
  userGroup: {
    type: string;
    active: boolean;
  };
  rank?: number;
  username: string;
  discord?: {
    avatar: string;
    id: string;
    username: string;
  };
};

export type Clip = {
  createdAt: string;
  game: string;
  id: string;
  internalId: string;
  isRemixable: boolean;
  reactions: Reaction[];
  shareId: string;
  thumbnailStandardUrl: string | undefined;
  thumbnailOverlayUrl: string | undefined;
  title: string;
  type: string;
  url: string;
  user: User;
  views: number;
};

export type ClipsArray = Clip[];

export const ProfileClips = ({
  isOwnProfile,
  user,
  userData,
  userLoading,
  userError,
  userNetworkStatus,
  pageInfo,
  paginationClipsNew,
  view,
  setClipInactive,
  hasFiltersSelected,
}: {
  isOwnProfile: boolean;
  view: EClipsViews;
  userLoading: boolean;
  userError: ApolloError | null | undefined;
  user: any;
  userData: any;
  userNetworkStatus: number;
  pageInfo: PageInfo;
  paginationClipsNew: () => void;
  setClipInactive: any;
  hasFiltersSelected: boolean;
}) => {
  if (userLoading && userNetworkStatus !== NetworkStatus.fetchMore) {
    return (
      <Flex alignItems={"center"} justifyContent={"center"}>
        <LoadingSpinner width={50} height={50} />
      </Flex>
    );
  }

  if (userError) {
    return <ErrorText>Error loading clips</ErrorText>;
  }

  const clips = userData?.clips?.nodes;
  const displayMatchHistoryWidget =
    isOwnProfile &&
    [EClipsViews.Clips, EClipsViews.Montages].includes(view) &&
    !hasFiltersSelected;

  return (
    <MainRail pb={[5, 5, 6]}>
      <PinnedClips
        isOwnProfile={isOwnProfile}
        username={user.username}
        displayArrows={false}
        userId={user._id}
      />
      <InfiniteScrollPagination
        isLoading={userLoading}
        onBottomHit={paginationClipsNew}
        loadOnMount={false}
        pageInfo={pageInfo}
      >
        {displayMatchHistoryWidget && (
          <Box mb="25px">
            <MatchHistoryWidget />
          </Box>
        )}
        {clips?.length === 0 && <NoFilterResultsContainer />}
        {clips?.map((clip: Clip, index: number) => {
          return (
            <>
              <Clip
                key={clip.id}
                clip={clip}
                isOwnProfile={isOwnProfile}
                isFirstClip={index === 0}
                isFeed={true}
                index={index}
                setClipInactive={setClipInactive}
                hasFiltersSelected={hasFiltersSelected}
                user={userData}
              />
            </>
          );
        })}
        {userNetworkStatus === NetworkStatus.fetchMore && (
          <Box style={{ paddingBottom: "80px" }}>
            <LoadingSkeleton height={500} style={{ borderRadius: "0px" }} />
            <Box my={3} />
            <Flex alignItems={"flex-start"} justifyContent="space-between">
              {" "}
              <Flex flexDirection={"column"}>
                <LoadingSkeleton
                  height={35}
                  width={300}
                  style={{ borderRadius: "10px" }}
                />
                <Box my={2} />
                <LoadingSkeleton
                  height={20}
                  width={60}
                  style={{ borderRadius: "6px" }}
                />{" "}
              </Flex>{" "}
              <LoadingSkeleton
                height={40}
                width={120}
                style={{ borderRadius: "999px" }}
              />
            </Flex>
            <Box my={3} />
            <Flex alignItems={"center"}>
              {" "}
              <LoadingSkeleton
                height={40}
                width={40}
                style={{ borderRadius: "999px" }}
              />{" "}
              <Box mx={2} />
              <LoadingSkeleton
                height={30}
                width={130}
                style={{ borderRadius: "10px" }}
              />
            </Flex>
          </Box>
        )}
      </InfiniteScrollPagination>
    </MainRail>
  );
};

const ErrorText = styled.p`
  color: ${({ theme }) => theme.colors.error};
`;
